import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, Combobox, ComboboxGroup, Input, Radio, Tooltip } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { ErrorDetails } from 'types/types'
import { BUSINESS_CUSTOMER_DEFAULT_FORM_DATA } from '../helpers/form'
import { BusinessCustomerFormData, ConfirmBusinessCustomerMethod } from '../helpers/types'
import { scrollToTopError } from '../helpers/utils'
import { useBusinessCustomerForm } from '../hooks/useBusinessCustomerForm'
import { AddCustomerActions } from './AddCustomerActions'

export interface AddBusinessCustomerProps {
  defaultValues: BusinessCustomerFormData
  onConfirm: ConfirmBusinessCustomerMethod
  onCancel: () => void
  fieldErrors?: ErrorDetails
}

export const AddBusinessCustomer = ({
  defaultValues,
  onConfirm,
  onCancel,
  fieldErrors,
}: AddBusinessCustomerProps) => {
  const { t } = useTranslation()
  const { accountCapabilities } = useAccountsContext()
  const { isRelianceModelCapabilityEnabled } = accountCapabilities || {}

  const { enableRelianceBusinessFields: enableRelianceBusinessFieldsFeatureFlag } =
    useFeatureFlags()

  const enableRelianceBusinessFields =
    isRelianceModelCapabilityEnabled && enableRelianceBusinessFieldsFeatureFlag

  const { formFields, displayData, hasErrors, handleSubmit, form } = useBusinessCustomerForm(
    defaultValues ?? BUSINESS_CUSTOMER_DEFAULT_FORM_DATA,
    fieldErrors,
    enableRelianceBusinessFields
  )
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})

  const onInvalid = () => {
    scrollToTopError(form, fieldRefs)
  }

  useEffect(() => {
    if (!hasErrors) return
    scrollToTopError(form, fieldRefs)
  }, [hasErrors, form?.formState?.errors])
  const onSubmit = (data: BusinessCustomerFormData) => {
    onConfirm({ data, displayData })
  }
  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Box flexCol gap={16}>
        <Box ref={el => (fieldRefs.current.businessName = el)}>
          <Input
            {...formFields?.businessName}
            placeholder={t('embeddedCustomers.businessFormPlaceholder.businessName')}
            testId="business-name"
          />
        </Box>
        <Box ref={el => (fieldRefs.current.businessDescription = el)}>
          <Input
            {...formFields?.businessDescription}
            testId="business-description"
            Tooltip={
              <Tooltip
                type="complex"
                bodyContent={t('embeddedCustomers.businessFormTooltip.businessDescription')}
              />
            }
          />
        </Box>
        <Box ref={el => (fieldRefs.current.industry = el)}>
          <ComboboxGroup {...formFields?.industry} />
        </Box>
        <Box ref={el => (fieldRefs.current.countryOfIncorporation = el)}>
          <Combobox {...formFields?.countryOfIncorporation} />
        </Box>
        <Box ref={el => (fieldRefs.current.monthlyExpectedVolumes = el)}>
          <Combobox {...formFields?.monthlyExpectedVolumes} />
        </Box>
        {enableRelianceBusinessFields && (
          <>
            <Box ref={el => (fieldRefs.current.riskScore = el)}>
              <Combobox
                {...formFields?.riskScore}
                optionalText={t('optional')}
                Tooltip={<Tooltip bodyContent={t('customers.riskScoreTooltip')} />}
              />
            </Box>
            <Box ref={el => (fieldRefs.current.eddCompleted = el)}>
              <Box flex align="center" gap={4} className="pb-2">
                <p className="text-label">{t('customers.eddPerformedLabel')}</p>
                <p className="text-body-regular-small">{t('optional')}</p>
                <Tooltip bodyContent={t('customers.eddPerformedTooltip')} />
              </Box>
              <Radio
                {...formFields?.eddCompleted}
                value={formFields?.eddCompleted?.value}
                error={formFields?.eddCompleted?.error}
              />
            </Box>
          </>
        )}
        <Box ref={el => (fieldRefs.current.useCase = el)}>
          <Combobox
            {...formFields?.useCase}
            Tooltip={<Tooltip type="complex" bodyContent={t('customers.serviceTypeTooltip')} />}
          />
        </Box>
        <Box ref={el => (fieldRefs.current.externalReference = el)}>
          <Input
            {...formFields?.externalReference}
            testId="business-external-reference"
            optionalText={t('optional')}
            Tooltip={
              <Tooltip type="complex" bodyContent={t('customers.externalReferenceTooltip')} />
            }
          />
        </Box>
      </Box>
      <AddCustomerActions onCancel={onCancel} isDisableContinue={hasErrors} />
    </form>
  )
}
